import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowUp, ArrowDown, X } from "lucide-react";

export const AccountSummary = ({ account, openTrades, currentPrice, onCloseTrade }) => {
  if (!account) {
    return (
      <div className="flex items-center justify-center h-32 text-zinc-500 text-sm">
        Loading account...
      </div>
    );
  }

  const totalUnrealizedPnl = openTrades.reduce((sum, trade) => {
    return sum + (trade.current_pnl || 0);
  }, 0);

  return (
    <div className="space-y-6" data-testid="account-summary">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-zinc-900 rounded border border-zinc-800 p-3">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Balance</div>
          <div className="font-mono text-lg font-semibold">${account.balance?.toFixed(2)}</div>
        </div>
        <div className="bg-zinc-900 rounded border border-zinc-800 p-3">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Equity</div>
          <div className="font-mono text-lg font-semibold">${account.equity?.toFixed(2)}</div>
        </div>
      </div>

      <div className="bg-zinc-900 rounded border border-zinc-800 p-3">
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Unrealized P&L</div>
        <div 
          className={`font-mono text-xl font-semibold ${totalUnrealizedPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}
          data-testid="unrealized-pnl"
        >
          {totalUnrealizedPnl >= 0 ? '+' : ''}${totalUnrealizedPnl.toFixed(2)}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-xs text-zinc-500 mb-1">Total</div>
          <div className="font-mono text-sm font-semibold">{account.total_trades || 0}</div>
        </div>
        <div>
          <div className="text-xs text-zinc-500 mb-1">Wins</div>
          <div className="font-mono text-sm font-semibold text-green-500">{account.winning_trades || 0}</div>
        </div>
        <div>
          <div className="text-xs text-zinc-500 mb-1">Losses</div>
          <div className="font-mono text-sm font-semibold text-red-500">{account.losing_trades || 0}</div>
        </div>
      </div>

      <div>
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-3">
          Open Positions ({openTrades.length})
        </div>
        
        {openTrades.length === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-sm">
            No open positions
          </div>
        ) : (
          <ScrollArea className="h-[200px]">
            <div className="space-y-2 pr-4">
              {openTrades.map((trade) => (
                <div key={trade.id} className="bg-zinc-900 rounded border border-zinc-800 p-3" data-testid={`position-${trade.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {trade.direction === "long" ? (
                        <ArrowUp className="w-4 h-4 text-green-500" />
                      ) : (
                        <ArrowDown className="w-4 h-4 text-red-500" />
                      )}
                      <span className={`font-mono text-xs uppercase font-semibold ${trade.direction === "long" ? "text-green-500" : "text-red-500"}`}>
                        {trade.direction}
                      </span>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-zinc-800" onClick={() => onCloseTrade(trade.id)} data-testid={`close-trade-${trade.id}`}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-zinc-500">Entry</span>
                      <div className="font-mono font-medium">${trade.entry_price?.toFixed(2)}</div>
                    </div>
                    <div>
                      <span className="text-zinc-500">Size</span>
                      <div className="font-mono font-medium">{trade.size} BTC</div>
                    </div>
                    <div>
                      <span className="text-zinc-500">Current</span>
                      <div className="font-mono font-medium">${currentPrice?.toFixed(2)}</div>
                    </div>
                    <div>
                      <span className="text-zinc-500">P&L</span>
                      <div className={`font-mono font-medium ${trade.current_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {trade.current_pnl >= 0 ? '+' : ''}${trade.current_pnl?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  {(trade.stop_price || trade.stop_loss || trade.target_price || trade.take_profit) && (
                    <div className="flex gap-4 mt-2 pt-2 border-t border-zinc-800 text-xs">
                      {(trade.stop_price || trade.stop_loss) && <div><span className="text-zinc-500">SL: </span><span className="font-mono text-red-500">${(trade.stop_price || trade.stop_loss)?.toFixed(2)}</span></div>}
                      {(trade.target_price || trade.take_profit) && <div><span className="text-zinc-500">TP: </span><span className="font-mono text-green-500">${(trade.target_price || trade.take_profit)?.toFixed(2)}</span></div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};
